import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_plugin_763fbfae from 'nuxt_plugin_plugin_763fbfae' // Source: ./components/plugin.js (mode: 'all')
import nuxt_plugin_gtm_44326b55 from 'nuxt_plugin_gtm_44326b55' // Source: ./gtm.js (mode: 'all')
import nuxt_plugin_axios_625a80bf from 'nuxt_plugin_axios_625a80bf' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_pluginutils_2a93a35c from 'nuxt_plugin_pluginutils_2a93a35c' // Source: ./nuxt-i18n/plugin.utils.js (mode: 'all')
import nuxt_plugin_pluginrouting_eb8e6cb2 from 'nuxt_plugin_pluginrouting_eb8e6cb2' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_5ac9db28 from 'nuxt_plugin_pluginmain_5ac9db28' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_badwordsclient_17d2c449 from 'nuxt_plugin_badwordsclient_17d2c449' // Source: ../plugins/bad-words.client.js (mode: 'client')
import nuxt_plugin_uicomponents_7c1399c4 from 'nuxt_plugin_uicomponents_7c1399c4' // Source: ../plugins/ui-components.js (mode: 'all')
import nuxt_plugin_tblloader_3dda0981 from 'nuxt_plugin_tblloader_3dda0981' // Source: ../plugins/tbl-loader.client (mode: 'client')
import nuxt_plugin_mixins_5013a1f0 from 'nuxt_plugin_mixins_5013a1f0' // Source: ../plugins/mixins (mode: 'all')
import nuxt_plugin_breadcrumbs_83e9e0cc from 'nuxt_plugin_breadcrumbs_83e9e0cc' // Source: ../plugins/breadcrumbs (mode: 'all')
import nuxt_plugin_window_1c8b23d3 from 'nuxt_plugin_window_1c8b23d3' // Source: ../plugins/window.client (mode: 'client')
import nuxt_plugin_clicknavigation_71dd63aa from 'nuxt_plugin_clicknavigation_71dd63aa' // Source: ../plugins/click-navigation.client (mode: 'client')
import nuxt_plugin_userpreferences_61d2348d from 'nuxt_plugin_userpreferences_61d2348d' // Source: ../plugins/user-preferences.client (mode: 'client')
import nuxt_plugin_lazyloadimage_eeae20d0 from 'nuxt_plugin_lazyloadimage_eeae20d0' // Source: ../plugins/directives/lazy-load-image (mode: 'all')
import nuxt_plugin_locationfinder_54873971 from 'nuxt_plugin_locationfinder_54873971' // Source: ../plugins/location-finder.client (mode: 'client')
import nuxt_plugin_previoussignincheck_053f8f2e from 'nuxt_plugin_previoussignincheck_053f8f2e' // Source: ../plugins/previous-sign-in-check.client (mode: 'client')
import nuxt_plugin_aditude_db8e15f2 from 'nuxt_plugin_aditude_db8e15f2' // Source: ../plugins/aditude.client (mode: 'client')
import nuxt_plugin_gtmevents_01f2039d from 'nuxt_plugin_gtmevents_01f2039d' // Source: ../plugins/gtm-events.client (mode: 'client')
import nuxt_plugin_buildmetadataobject_fb99d554 from 'nuxt_plugin_buildmetadataobject_fb99d554' // Source: ../plugins/build-meta-data-object.server-client (mode: 'all')
import nuxt_plugin_ga4init_26089500 from 'nuxt_plugin_ga4init_26089500' // Source: ../plugins/ga4-init.client (mode: 'client')
import nuxt_plugin_pgcounttracker_f7b8e65e from 'nuxt_plugin_pgcounttracker_f7b8e65e' // Source: ../plugins/pg_count-tracker.client (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"wordfinder","htmlAttrs":{"lang":"en"},"meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"hid":"description","name":"description","content":""}],"link":[{"rel":"preload","as":"script","href":"https:\u002F\u002Fwww.googletagservices.com\u002Ftag\u002Fjs\u002Fgpt.js"},{"hid":"favicon-png-1","rel":"icon","type":"image\u002Fpng","sizes":"16x16","href":"\u002Fwfw-favicon-16.png"},{"hid":"favicon-png-2","rel":"icon","type":"image\u002Fpng","sizes":"32x32","href":"\u002Fwfw-favicon-32.png"},{"hid":"favicon-png-3","rel":"icon","type":"image\u002Fpng","sizes":"180x180","href":"\u002Fwfw-favicon-180.png"},{"hid":"favicon-png-4","rel":"icon","type":"image\u002Fpng","sizes":"512x512","href":"\u002Fwfw-favicon-512.png"},{"hid":"favicon-ico","rel":"icon","type":"image\u002Fx-icon","sizes":"256x256","href":"\u002Ffavicon.ico"}],"script":[{"innerHTML":"window.dataLayer = window.dataLayer || [];","type":"text\u002Fjavascript","charset":"utf-8","async":true},{"id":"wf_openweb","src":"https:\u002F\u002Flauncher-sa.spot.im\u002Fspot\u002Fsp_tpllDQEf","async":true,"data-spotim-module":"spotim-launcher","data-spotim-autorun":false,"type":"text\u002Fplain","class":"optanon-category-C0004"},{"type":"text\u002Fjavascript","innerHTML":"window.onpageshow = function(event) {if (event.persisted) {window.location.reload();}};"},{"hid":"jsonld-organization","type":"application\u002Fld+json","json":{"@context":"https:\u002F\u002Fschema.org","@type":"Organization","name":"WordFinder by YourDictionary","url":"https:\u002F\u002Fwordfinder.yourdictionary.com","logo":"https:\u002F\u002Fwordfinder.yourdictionary.com\u002Fstatic\u002Fimages\u002Fic_wfw_complete.png","address":{"@type":"PostalAddress","addressLocality":"Vero Beach, FL","postalCode":"32960-4706","streetAddress":"1275 US Highway 1 Unit 2-6084"},"email":"wordfinder@yourdictionary.com","sameAs":["https:\u002F\u002Fwww.facebook.com\u002FWordFinderbyYD\u002F","https:\u002F\u002Fx.com\u002Fwordfinderbyyd","https:\u002F\u002Fwww.instagram.com\u002Fwordfinderbyyd\u002F"],"description":"Scrabble Word Finder from YourDictionary is the perfect word finder for playing the new word game Scrabble GO. Score more points and win more often with our free Scrabble cheat tool.","parentOrganization":{"@type":"Organization","@id":"https:\u002F\u002Fwww.lovetoknowmedia.com\u002F","name":"LoveToKnow Media","url":"https:\u002F\u002Fwww.lovetoknowmedia.com\u002F","sameAs":["https:\u002F\u002Fwww.linkedin.com\u002Fcompany\u002Flovetoknowmedia\u002F","https:\u002F\u002Fwww.instagram.com\u002Flovetoknowmedia\u002F","https:\u002F\u002Fwww.facebook.com\u002Flovetoknowmedia\u002Fabout","https:\u002F\u002Fx.com\u002Flovetoknowmedia","https:\u002F\u002Fwww.crunchbase.com\u002Forganization\u002Flovetoknow-corp"],"logo":{"@type":"ImageObject","url":"https:\u002F\u002Fwww.lovetoknowmedia.com\u002Fwp-content\u002Fuploads\u002F2021\u002F05\u002FLTK_MEDIA_Logo-2.png"},"address":{"@type":"PostalAddress","addressLocality":"Vero Beach, FL","postalCode":"32960-4706","streetAddress":"1275 US Highway 1 Unit 2-6084"}}}},{"hid":"gtm-script","innerHTML":"if(!window._gtm_init){window._gtm_init=1;(function(w,n,d,m,e,p){w[d]=(w[d]==1||n[d]=='yes'||n[d]==1||n[m]==1||(w[e]&&w[e][p]&&w[e][p]()))?1:0})(window,navigator,'doNotTrack','msDoNotTrack','external','msTrackingProtectionEnabled');(function(w,d,s,l,x,y){w[x]={};w._gtm_inject=function(i){if(w.doNotTrack||w[x][i])return;w[x][i]=1;w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.async=true;j.src='https:\u002F\u002Fwww.googletagmanager.com\u002Fgtm.js?id='+i;f.parentNode.insertBefore(j,f);};w[y]('GTM-5TPB9NP')})(window,document,'script','dataLayer','_gtm_ids','_gtm_inject')}"}],"style":[],"noscript":[{"hid":"gtm-noscript","pbody":true,"innerHTML":"\u003Ciframe src=\"https:\u002F\u002Fwww.googletagmanager.com\u002Fns.html?id=GTM-5TPB9NP&\" height=\"0\" width=\"0\" style=\"display:none;visibility:hidden\" title=\"gtm\"\u003E\u003C\u002Fiframe\u003E"}],"__dangerouslyDisableSanitizersByTagID":{"gtm-script":["innerHTML"],"gtm-noscript":["innerHTML"]}},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_plugin_763fbfae === 'function') {
    await nuxt_plugin_plugin_763fbfae(app.context, inject)
  }

  if (typeof nuxt_plugin_gtm_44326b55 === 'function') {
    await nuxt_plugin_gtm_44326b55(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_625a80bf === 'function') {
    await nuxt_plugin_axios_625a80bf(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginutils_2a93a35c === 'function') {
    await nuxt_plugin_pluginutils_2a93a35c(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginrouting_eb8e6cb2 === 'function') {
    await nuxt_plugin_pluginrouting_eb8e6cb2(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_5ac9db28 === 'function') {
    await nuxt_plugin_pluginmain_5ac9db28(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_badwordsclient_17d2c449 === 'function') {
    await nuxt_plugin_badwordsclient_17d2c449(app.context, inject)
  }

  if (typeof nuxt_plugin_uicomponents_7c1399c4 === 'function') {
    await nuxt_plugin_uicomponents_7c1399c4(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_tblloader_3dda0981 === 'function') {
    await nuxt_plugin_tblloader_3dda0981(app.context, inject)
  }

  if (typeof nuxt_plugin_mixins_5013a1f0 === 'function') {
    await nuxt_plugin_mixins_5013a1f0(app.context, inject)
  }

  if (typeof nuxt_plugin_breadcrumbs_83e9e0cc === 'function') {
    await nuxt_plugin_breadcrumbs_83e9e0cc(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_window_1c8b23d3 === 'function') {
    await nuxt_plugin_window_1c8b23d3(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_clicknavigation_71dd63aa === 'function') {
    await nuxt_plugin_clicknavigation_71dd63aa(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_userpreferences_61d2348d === 'function') {
    await nuxt_plugin_userpreferences_61d2348d(app.context, inject)
  }

  if (typeof nuxt_plugin_lazyloadimage_eeae20d0 === 'function') {
    await nuxt_plugin_lazyloadimage_eeae20d0(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_locationfinder_54873971 === 'function') {
    await nuxt_plugin_locationfinder_54873971(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_previoussignincheck_053f8f2e === 'function') {
    await nuxt_plugin_previoussignincheck_053f8f2e(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_aditude_db8e15f2 === 'function') {
    await nuxt_plugin_aditude_db8e15f2(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_gtmevents_01f2039d === 'function') {
    await nuxt_plugin_gtmevents_01f2039d(app.context, inject)
  }

  if (typeof nuxt_plugin_buildmetadataobject_fb99d554 === 'function') {
    await nuxt_plugin_buildmetadataobject_fb99d554(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_ga4init_26089500 === 'function') {
    await nuxt_plugin_ga4init_26089500(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_pgcounttracker_f7b8e65e === 'function') {
    await nuxt_plugin_pgcounttracker_f7b8e65e(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
